import { useState, useEffect } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import SideSection from '../constant/SideSection';
import { useNavigate } from 'react-router-dom';

export default function PasswordUpdateForm() {
  const [currentStep, setCurrentStep] = useState(1); // 1 = email step, 2 = password step
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  
  // Password validation states
  const [validations, setValidations] = useState({
    minLength: false,
    hasNumber: false,
    hasUppercase: false,
    hasLowercase: false
  });

  // Check password validations
  useEffect(() => {
    setValidations({
      minLength: newPassword.length >= 8,
      hasNumber: /\d/.test(newPassword),
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword)
    });
  }, [newPassword]);

  const ValidationItem = ({ isValid, text }) => (
    <div className="flex items-center space-x-2">
      {isValid ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <X className="w-4 h-4 text-red-500" />
      )}
      <span className={`text-sm ${isValid ? 'text-green-600' : 'text-red-600'}`}>
        {text}
      </span>
    </div>
  );

  const isEmailValid = registeredEmail.length > 0 && registeredEmail.includes('@');
  const isPasswordFormValid = Object.values(validations).every(Boolean) && 
                             newPassword === confirmPassword && 
                             confirmPassword.length > 0;

  const handleEmailSubmit = () => {
    if (isEmailValid) {
      setCurrentStep(2);
    }
  };

  return (
    <div className='flex flex-col lg:flex-row min-h-screen'>
      {/* Side Section - only visible on large screens */}
      <div className="hidden w-1/2 lg:block">
        <SideSection imageUrl="/images/auth.png" />
      </div>
      <div className="flex-1 lg:w-1/2 flex flex-col min-h-screen">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-[30rem] p-8 shadow-sm">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="flex justify-center mb-6">
                <div>
                    <img src="/images/logo.png" className='w-20' alt="Logo" />
                </div>
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-3">
                {currentStep === 1 ? 'Reset your password' : 'Update your password'}
                </h1>
                <p className="text-gray-600 text-sm leading-relaxed">
                {currentStep === 1 
                    ? 'Enter your registered email address, and we\'ll send you password reset instructions.'
                    : 'Set your new password with a minimum of 8 characters, including a combination of letters and numbers.'
                }
                </p>
            </div>

            {/* Form */}
            <div className="space-y-6">
                {currentStep === 1 ? (
                // Step 1: Email Form
                <>
                    <div>
                    <div className="block text-sm font-medium text-gray-700 mb-2">
                        Registered Email <span className="text-red-500">*</span>
                    </div>
                    <input
                        type="email"
                        value={registeredEmail}
                        onChange={(e) => setRegisteredEmail(e.target.value)}
                        placeholder="Input your registered email address"
                        className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400"
                    />
                    </div>

                    <button
                    onClick={handleEmailSubmit}
                    disabled={!isEmailValid}
                    className="w-full bg-purple-800 text-white py-3 px-4 rounded-md font-medium hover:bg-purple-900 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    Submit
                    </button>

                    <button
                    onClick={() => navigate('/auth/login')}
                    className="w-full bg-white text-purple-800 py-3 px-4 rounded-md font-medium border-2 border-purple-800 hover:bg-purple-50 transition duration-200"
                    >
                    Back To Login
                    </button>

                    <div className="text-center pt-2">
                    <span className="text-gray-600 text-sm">New Here? </span>
                    <button onClick={()=>{navigate('/auth/register')}} className="text-purple-600 text-sm font-medium hover:underline">
                        Create an Account
                    </button>
                    </div>
                </>
                ) : (
                // Step 2: Password Form
                <>
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-700">
                        Password reset instructions sent to: <strong>{registeredEmail}</strong>
                    </p>
                    </div>

                    <div>
                    <div className="block text-sm font-medium text-gray-700 mb-2">
                        New Password <span className="text-red-500">*</span>
                    </div>
                    <div className="relative">
                        <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••••"
                        className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400"
                        />
                        <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    </div>

                    {newPassword.length > 0 && (
                    <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                        <ValidationItem 
                        isValid={validations.minLength} 
                        text="8 characters" 
                        />
                        <ValidationItem 
                        isValid={validations.hasNumber} 
                        text="Number (0-9)" 
                        />
                        <ValidationItem 
                        isValid={validations.hasUppercase} 
                        text="Uppercase letter (A-Z)" 
                        />
                        <ValidationItem 
                        isValid={validations.hasLowercase} 
                        text="Lowercase letter (a-z)" 
                        />
                    </div>
                    )}

                    <div>
                    <div className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmation New Password <span className="text-red-500">*</span>
                    </div>
                    <div className="relative">
                        <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-type your new password"
                        className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400"
                        />
                        <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    {confirmPassword && newPassword !== confirmPassword && (
                        <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                    )}
                    </div>

                    <button
                    onClick={() => alert('Password updated successfully')}
                    disabled={!isPasswordFormValid}
                    className="w-full bg-purple-800 text-white py-3 px-4 rounded-md font-medium hover:bg-purple-900 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                    Update Password
                    </button>

                    <button
                    onClick={() => setCurrentStep(1)}
                    className="w-full bg-white text-purple-800 py-3 px-4 rounded-md font-medium border-2 border-purple-800 hover:bg-purple-50 transition duration-200"
                    >
                    Back to Email
                    </button>
                </>
                )}
            </div>
            </div>
        </div>

        {/* Footer */}
        <div className="mt-8">
            <div className="p-6 border-t border-gray-200 bg-white">
            <div className="text-center text-xs text-gray-500 space-x-4">
                <span>© 2024 GPay. All rights reserved.</span>
                <a href="#" className="hover:text-gray-700">Terms & Conditions</a>
                <a href="#" className="hover:text-gray-700">Privacy Policy</a>
            </div>
            </div>
        </div>
        </div>
    </div>
  );
}